import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useFCMToken } from './hooks/useFCMToken';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { ytor } from './theme/ytor';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Checkbox, FormControlLabel,
  CircularProgress, Alert, Divider
} from '@mui/material';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase/config';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import Tags from './pages/Tags';
import CVPreview from './pages/CVPreview';
import CompanySettings from './pages/CompanySettings';
import Projects from './pages/Projects';
import Tenders from './pages/Tenders';
import TenderTeam from './pages/TenderTeam';
import TenderVariant from './pages/TenderVariant';
import TenderCVPrint from './pages/TenderCVPrint';
import Landing from './pages/Landing';
import AppSettings from './pages/AppSettings';
import JoinPage from './pages/JoinPage';
import ReferenceProjects from './pages/ReferenceProjects';
import ReferenceProjectPreview from './pages/ReferenceProjectPreview';
import SkillsManager from './pages/SkillsManager';
import GapAnalysis from './pages/GapAnalysis';
import FeedbackPage from './pages/FeedbackPage';
import FeedbackAdmin from './pages/FeedbackAdmin';
import SubcontractorList from './pages/SubcontractorList';
import DocumentLibrary from './pages/DocumentLibrary';
import SubcontractorProfile from './pages/SubcontractorProfile';
import SubcontractorCVPreview from './pages/SubcontractorCVPreview';
import ReferenceVariant from './pages/ReferenceVariant';
import Affarer from './pages/Affarer';
import AffärerStatistik from './pages/AffärerStatistik';
import AffärerKalender from './pages/AffärerKalender';
import AffärerKarta from './pages/AffärerKarta';
import Products from './pages/Products';
import FlatboxPage from './pages/FlatboxPage';
import { canUseFlatbox } from './utils/permissions';
import CRMCompanies from './pages/CRMCompanies';
import CRMCompany from './pages/CRMCompany';
import CRMLeads from './pages/CRMLeads';
import CRMLead from './pages/CRMLead';
import CRMKundprojekt from './pages/CRMKundprojekt';
import CRMReminders from './pages/CRMReminders';
import PlingKlang from './pages/PlingKlang';
import Offers from './pages/Offers';
import OffersDashboard from './pages/OffersDashboard';
import OfferAterkoppling from './pages/OfferAterkoppling';
import OfferEditor from './pages/OfferEditor';
import OfferTemplates from './pages/OfferTemplates';
import OfferBudgetEditor from './pages/OfferBudgetEditor';
import ProjectBudget from './pages/ProjectBudget';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import BudgetReport from './pages/BudgetReport';
import BudgetN from './pages/BudgetN';
import FramtidN from './pages/FramtidN';
import ExternTid from './pages/ExternTid';
import Marketing from './pages/Marketing';
import QRKodN from './pages/QRKodN';
import Signature from './pages/Signature';
import LinkedInVerkstad from './pages/LinkedInVerkstad';
import LinkedInPostEditor from './pages/LinkedInPostEditor';
import LinkedInKampanjer from './pages/LinkedInKampanjer';
import HR from './pages/HR';
import EmployeeDirectory from './pages/EmployeeDirectory';
import IUP from './pages/IUP';
import IUPTeam from './pages/IUPTeam';
import IUPTemplateEditor from './pages/IUPTemplateEditor';
import IUPExpectations from './pages/IUPExpectations';
import Avgangstavla from './pages/Avgangstavla';
import WorkshopNAdmin from './modules/workshop/pages/WorkshopNAdmin';
import WorkshopNDesign from './modules/workshop/pages/WorkshopNDesign';
import WorkshopNParticipant from './modules/workshop/pages/WorkshopNParticipant';
import WorkshopNPresent from './modules/workshop/pages/WorkshopNPresent';
import WorkshopNIntro from './modules/workshop/pages/WorkshopNIntro';
import WorkshopNSchedule from './modules/workshop/pages/WorkshopNSchedule';
import WorkshopNEnkät from './modules/workshop/pages/WorkshopNEnkät';
import WorkshopJoin from './pages/WorkshopJoin';
import EnkätSida from './pages/EnkätSida';
import PublicView from './modules/workshop/components/IntroPresenter/PublicView';
import SchedulePublicView from './modules/workshop/components/IntroPresenter/SchedulePublicView';
import ScheduleSpeakerView from './modules/workshop/components/IntroPresenter/ScheduleSpeakerView';
import WorkshopDisplay from './modules/workshop/components/WorkshopDisplay';
import WorkshopNPrint from './modules/workshop/pages/WorkshopNPrint';
import WorkshopNKiosk from './modules/workshop/pages/WorkshopNKiosk';
import WorkshopScan from './modules/workshop/pages/WorkshopScan';
import SwotNAdmin from './modules/swot/pages/SwotNAdmin';
import SwotNBoard from './modules/swot/pages/SwotNBoard';
import SwotNDesign from './modules/swot/pages/SwotNDesign';
import SwotNPrint from './modules/swot/pages/SwotNPrint';
import SwotNScan from './modules/swot/pages/SwotNScan';
import PitchList from './modules/pitch/PitchList';
import PitchBuilder from './modules/pitch/PitchBuilder';
import PitchBackgrounds from './modules/pitch/PitchBackgrounds';
import PresenterView from './modules/pitch/PresenterView';
import AudienceView from './modules/pitch/AudienceView';
import PublicPitchView from './modules/pitch/PublicPitchView';
import ExternalPitchView from './modules/pitch/ExternalPitchView';
import PitchReview       from './modules/pitch/PitchReview';
import PublicOfferView from './pages/PublicOfferView';
import Upphandlingar from './pages/Upphandlingar';
import AppLabLanding from './modules/applab/pages/AppLabLanding';
import AppLabAdmin from './modules/applab/admin/AdminPage';
import AppLabTestGateway from './modules/applab/pages/TestGatewayPage';
import AppLabUpload from './modules/applab/pages/UploadPage';
import AppLabDocs from './modules/applab/pages/DocsPage';
import NewAppChoice from './modules/applab/pages/NewAppChoice';
import MockupLanding from './modules/applab/mockupn/pages/MockupLanding';
import MockupWizard from './modules/applab/mockupn/pages/MockupWizard';
import MockupGenerating from './modules/applab/mockupn/pages/MockupGenerating';
import MockupEditor from './modules/applab/mockupn/pages/MockupEditor';
import MappNLanding from './modules/applab/mappn/pages/MappNLanding';
import MappNCards from './modules/applab/mappn/pages/MappNCards';
import MappNSettings from './modules/applab/mappn/pages/MappNSettings';
import MappNPerspectives from './modules/applab/mappn/pages/MappNPerspectives';
import MappNMap from './modules/applab/mappn/pages/MappNMap';
import MappNTable from './modules/applab/mappn/pages/MappNTable';
import MappNTech from './modules/applab/mappn/pages/MappNTech';
import Contracts from './pages/Contracts';
import OrderConfirmations from './pages/OrderConfirmations';
import OrderConfirmationEditor from './pages/OrderConfirmationEditor';
import Recruitment from './pages/Recruitment';
import Onboarding from './pages/Onboarding';
import OnboardingDetail from './pages/OnboardingDetail';
import OnboardingTemplateEditor from './pages/OnboardingTemplateEditor';
import CastNDisplay from './pages/CastNDisplay';
import CastNConnect from './pages/CastNConnect';
import CastNCasts from './pages/CastNCasts';
import CastNSlideEditor from './pages/CastNSlideEditor';
import CastNDisplays from './pages/CastNDisplays';
import CastNDashboard from './pages/CastNDashboard';
import CastNSlidePreview from './pages/CastNSlidePreview';
import CastNBackgrounds from './pages/CastNBackgrounds';

function PrivateRoute({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  return currentUser ? children : <Navigate to="/login" />;
}

// Public presenter view — no auth required
function PublicViewRoute() {
  const { id } = useParams();
  return <PublicView sessionId={id} />;
}

// Public schedule helskärm — no auth required
function SchedulePublicViewRoute() {
  const { id } = useParams();
  return <SchedulePublicView sessionId={id} />;
}

// Schedule speaker view — no auth required (facilitator opens in new window)
function ScheduleSpeakerViewRoute() {
  const { id } = useParams();
  return <ScheduleSpeakerView sessionId={id} />;
}

// Workshop display — the single HDMI window, no auth required
function WorkshopDisplayRoute() {
  const { id } = useParams();
  return <WorkshopDisplay sessionId={id} />;
}

function RoleRoute({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  if (!currentUser) return <Navigate to="/login" />;
  return children;
}

function FlatboxRoute({ children }) {
  const { currentUser, userProfile, loading } = useAuth();
  if (loading) return null;
  if (!currentUser) return <Navigate to="/login" />;
  if (!canUseFlatbox(userProfile)) return <Navigate to="/dashboard" />;
  return children;
}

function GDPRConsent({ onAccepted }) {
  const { currentUser } = useAuth();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  async function handleAccept() {
    if (!checked) return;
    setSaving(true);
    await updateDoc(doc(db, 'users', currentUser.uid), {
      gdprConsent: true,
      gdprConsentDate: new Date(),
      gdprConsentVersion: '1.0'
    });
    setSaving(false);
    onAccepted();
  }

  return (
    <Dialog open maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <GppMaybeIcon sx={{ color: '#3949ab', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Samtycke till behandling av personuppgifter
            </Typography>
            <Typography variant="caption" color="text.secondary">
              GDPR – Dataskyddsförordningen
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }} icon={false}>
          <Typography variant="body2">
            Innan du börjar använda LagbasN behöver vi ditt samtycke
            till hur vi hanterar dina personuppgifter.
          </Typography>
        </Alert>

        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Vilka uppgifter lagrar vi?
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2 }}>
          {[
            'Namn och e-postadress (från ditt Microsoft 365-konto)',
            'Profilbild (om du laddar upp en)',
            'Yrkesuppgifter: titel, uppdrag, utbildning, kompetenser',
            'CV-information for konsultmatchning och anbudsarbete',
          ].map((item, i) => (
            <li key={i}>
              <Typography variant="body2">{item}</Typography>
            </li>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Hur anvander vi uppgifterna?
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2 }}>
          {[
            'Intern konsultmatchning för anbud inom er organisation',
            'Generering av CV och konsultprofiler för anbudsunderlag',
            'Administration av anbudsteam och projektdeltagare',
          ].map((item, i) => (
            <li key={i}>
              <Typography variant="body2">{item}</Typography>
            </li>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Var lagras data?
        </Typography>
        <Typography variant="body2" gutterBottom>
          All data lagras i Google Firebase inom EU
          (europe-north1, Finland). Data delas inte med
          tredje part och används inte för marknadsföring.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Dina rättigheter
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2 }}>
          {[
            'Rätt att se vilken data vi har om dig',
            'Rätt att rätta felaktig information',
            'Rätt att bli glömd – begäran om radering hanteras av din administratör',
            'Rätt att återkalla samtycke – kontakta din administratör',
          ].map((item, i) => (
            <li key={i}>
              <Typography variant="body2">{item}</Typography>
            </li>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{
          p: 2, bgcolor: '#f3f4ff', borderRadius: 2,
          border: '1px solid #c5cae9'
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={e => setChecked(e.target.checked)}
                sx={{ '&.Mui-checked': { color: '#3949ab' } }}
              />
            }
            label={
              <Typography variant="body2" fontWeight="bold">
                Jag har läst och godkänner att LagbasN behandlar
                mina personuppgifter i enlighet med GDPR och
                beskrivningen ovan.
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button variant="contained"
          disabled={!checked || saving}
          startIcon={saving
            ? <CircularProgress size={16} color="inherit" />
            : <GppMaybeIcon />}
          onClick={handleAccept}
          sx={{
            bgcolor: checked ? '#3949ab' : undefined,
            '&:hover': { bgcolor: '#283593' }
          }}>
          {saving ? 'Sparar...' : 'Jag godkänner – Gå vidare'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GDPRGate({ children }) {
  const { currentUser, loading: authLoading } = useAuth();
  const [consentStatus, setConsentStatus] = useState('loading');

  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) {
      setConsentStatus('not-needed');
      return;
    }
    async function checkConsent() {
      const snap = await getDoc(doc(db, 'users', currentUser.uid));
      if (snap.exists() && snap.data()?.gdprConsent === true) {
        setConsentStatus('accepted');
      } else {
        setConsentStatus('needed');
      }
    }
    checkConsent();
  }, [currentUser, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (consentStatus === 'loading') return null;
  if (consentStatus === 'needed') {
    return (
      <>
        {children}
        <GDPRConsent onAccepted={() => setConsentStatus('accepted')} />
      </>
    );
  }
  return children;
}

// Registrerar FCM-token för inloggad superadmin (tyst no-op om VAPID saknas)
function FCMRegistrar() {
  const { currentUser } = useAuth();
  useFCMToken(currentUser?.uid);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/dashboard" element={
        <RoleRoute><Dashboard /></RoleRoute>
      } />
      <Route path="/profile" element={
        <PrivateRoute><Profile /></PrivateRoute>
      } />
      <Route path="/profile/:uid" element={
        <PrivateRoute><Profile /></PrivateRoute>
      } />
      <Route path="/admin" element={
        <RoleRoute><Admin /></RoleRoute>
      } />
      <Route path="/tags" element={
        <RoleRoute><Tags /></RoleRoute>
      } />
      <Route path="/cv-preview" element={
        <RoleRoute><CVPreview /></RoleRoute>
      } />
      <Route path="/cv-preview/:userId" element={
        <RoleRoute><CVPreview /></RoleRoute>
      } />
      <Route path="/company" element={
        <RoleRoute><CompanySettings /></RoleRoute>
      } />
      <Route path="/projects-legacy" element={
        <RoleRoute><Projects /></RoleRoute>
      } />
      <Route path="/tenders" element={
        <RoleRoute><Tenders /></RoleRoute>
      } />
      <Route path="/upphandlingar" element={
        <RoleRoute><Upphandlingar /></RoleRoute>
      } />
      <Route path="/avtal" element={
        <RoleRoute><Contracts /></RoleRoute>
      } />
      <Route path="/orderbekraftelser" element={
        <RoleRoute><OrderConfirmations /></RoleRoute>
      } />
      <Route path="/orderbekraftelser/:id" element={
        <RoleRoute><OrderConfirmationEditor /></RoleRoute>
      } />
      <Route path="/rekrytering" element={
        <RoleRoute><Recruitment /></RoleRoute>
      } />
      <Route path="/onboarding" element={
        <RoleRoute><Onboarding /></RoleRoute>
      } />
      <Route path="/onboarding/:id" element={
        <RoleRoute><OnboardingDetail /></RoleRoute>
      } />
      <Route path="/onboarding-mall" element={
        <RoleRoute><OnboardingTemplateEditor /></RoleRoute>
      } />
      {/* CastN – digital signage */}
      <Route path="/display/:token" element={<CastNDisplay />} />
      <Route path="/castn/connect" element={<PrivateRoute><CastNConnect /></PrivateRoute>} />
      <Route path="/marketing/castn" element={<RoleRoute><CastNDashboard /></RoleRoute>} />
      <Route path="/marketing/castn/casts" element={<RoleRoute><CastNCasts /></RoleRoute>} />
      <Route path="/marketing/castn/casts/:castId" element={<RoleRoute><CastNSlideEditor /></RoleRoute>} />
      <Route path="/marketing/castn/displays" element={<RoleRoute><CastNDisplays /></RoleRoute>} />
      <Route path="/castn/preview/:slideId" element={<PrivateRoute><CastNSlidePreview /></PrivateRoute>} />
      <Route path="/marketing/castn/backgrounds" element={<RoleRoute><CastNBackgrounds /></RoleRoute>} />
      <Route path="/tenders/:tenderId/team" element={
        <RoleRoute><TenderTeam /></RoleRoute>
      } />
      <Route path="/tenders/:tenderId/variant/:userId" element={
        <RoleRoute><TenderVariant /></RoleRoute>
      } />
      <Route path="/tenders/:tenderId/cv-print" element={
        <RoleRoute><TenderCVPrint /></RoleRoute>
      } />
      <Route path="/settings" element={
        <RoleRoute><AppSettings /></RoleRoute>
      } />
      <Route path="/reference-projects" element={
        <RoleRoute><ReferenceProjects /></RoleRoute>
      } />
      <Route path="/reference-projects/:projectId/preview" element={
        <RoleRoute><ReferenceProjectPreview /></RoleRoute>
      } />
      <Route path="/skills" element={
        <RoleRoute><SkillsManager /></RoleRoute>
      } />
      <Route path="/gap-analysis" element={
        <RoleRoute><GapAnalysis /></RoleRoute>
      } />
      <Route path="/feedback" element={
        <RoleRoute><FeedbackPage /></RoleRoute>
      } />
      <Route path="/feedback-admin" element={
        <RoleRoute><FeedbackAdmin /></RoleRoute>
      } />
      <Route path="/document-library" element={
        <RoleRoute><DocumentLibrary /></RoleRoute>
      } />
      <Route path="/subcontractors" element={
        <RoleRoute><SubcontractorList /></RoleRoute>
      } />
      <Route path="/subcontractors/:subId" element={
        <RoleRoute><SubcontractorProfile /></RoleRoute>
      } />
      <Route path="/subcontractors/:subId/preview" element={
        <RoleRoute><SubcontractorCVPreview /></RoleRoute>
      } />
      <Route path="/tenders/:tenderId/reference-variant/:refId" element={
        <RoleRoute><ReferenceVariant /></RoleRoute>
      } />
      {/* Affärer – samlad sektion med kunder, pipeline, produkter och PLING */}
      <Route path="/affarer" element={
        <RoleRoute><Affarer /></RoleRoute>
      } />
      <Route path="/affarer/statistik" element={
        <RoleRoute><AffärerStatistik /></RoleRoute>
      } />
      <Route path="/affarer/kalender" element={
        <RoleRoute><AffärerKalender /></RoleRoute>
      } />
      <Route path="/affarer/karta" element={
        <RoleRoute><AffärerKarta /></RoleRoute>
      } />
      <Route path="/produkter" element={
        <RoleRoute><Products /></RoleRoute>
      } />
      <Route path="/flatbox" element={
        <FlatboxRoute><FlatboxPage /></FlatboxRoute>
      } />
      <Route path="/affarer/flatbox" element={<Navigate to="/flatbox" replace />} />
      {/* CRM – åtkomst för alla inkl. marknadskonsulter */}
      <Route path="/crm/companies" element={
        <RoleRoute><CRMCompanies /></RoleRoute>
      } />
      <Route path="/crm/companies/:companyId" element={
        <RoleRoute><CRMCompany /></RoleRoute>
      } />
      <Route path="/crm/leads" element={
        <RoleRoute><CRMLeads /></RoleRoute>
      } />
      <Route path="/crm/leads/:leadId" element={
        <RoleRoute><CRMLead /></RoleRoute>
      } />
      {/* Kundprojekt – kundens projekt som säljunderlag, inte /projects (Tikabs uppdrag) */}
      <Route path="/crm/kundprojekt/:kundprojektId" element={
        <RoleRoute><CRMKundprojekt /></RoleRoute>
      } />
      <Route path="/crm/reminders" element={
        <RoleRoute><CRMReminders /></RoleRoute>
      } />
      <Route path="/crm/pling-klang" element={
        <RoleRoute><PlingKlang /></RoleRoute>
      } />
      <Route path="/offers" element={
        <RoleRoute><Offers /></RoleRoute>
      } />
      <Route path="/offers/dashboard" element={
        <RoleRoute><OffersDashboard /></RoleRoute>
      } />
      <Route path="/offers/aterkoppling" element={
        <RoleRoute><OfferAterkoppling /></RoleRoute>
      } />
      <Route path="/offers/:offerId" element={
        <RoleRoute><OfferEditor /></RoleRoute>
      } />
      <Route path="/offers/:offerId/budget" element={
        <RoleRoute><ProjectBudget /></RoleRoute>
      } />
      <Route path="/offers/:offerId/budget-edit" element={
        <RoleRoute><OfferBudgetEditor /></RoleRoute>
      } />
      <Route path="/offer-templates" element={
        <RoleRoute><OfferTemplates /></RoleRoute>
      } />
      <Route path="/projects" element={
        <RoleRoute><ProjectList /></RoleRoute>
      } />
      <Route path="/projects/:projectId" element={
        <RoleRoute><ProjectDetail /></RoleRoute>
      } />
      <Route path="/projects/:projectId/budget-report" element={
        <RoleRoute><BudgetReport /></RoleRoute>
      } />
      <Route path="/framtidn" element={
        <RoleRoute><FramtidN /></RoleRoute>
      } />
      <Route path="/externtid" element={
        <RoleRoute><ExternTid /></RoleRoute>
      } />
      <Route path="/budgetn" element={
        <RoleRoute><BudgetN /></RoleRoute>
      } />
      {/* Marketing – åtkomst för alla inkl. marknadskonsulter */}
      <Route path="/marketing" element={
        <RoleRoute><Marketing /></RoleRoute>
      } />
      <Route path="/marketing/qrkod" element={
        <RoleRoute><QRKodN /></RoleRoute>
      } />
      <Route path="/marketing/signature" element={
        <RoleRoute><Signature /></RoleRoute>
      } />
      <Route path="/marketing/linkedin" element={
        <RoleRoute><LinkedInVerkstad /></RoleRoute>
      } />
      <Route path="/marketing/linkedin/kampanjer" element={
        <RoleRoute><LinkedInKampanjer /></RoleRoute>
      } />
      <Route path="/marketing/linkedin/:postId" element={
        <RoleRoute><LinkedInPostEditor /></RoleRoute>
      } />
      {/* HR – ej för marknadskonsulter */}
      <Route path="/hr" element={
        <RoleRoute><HR /></RoleRoute>
      } />
      <Route path="/employees" element={
        <RoleRoute><EmployeeDirectory /></RoleRoute>
      } />
      <Route path="/hr/iup" element={
        <RoleRoute><IUP /></RoleRoute>
      } />
      <Route path="/hr/iup/:iupId" element={
        <RoleRoute><IUP /></RoleRoute>
      } />
      <Route path="/hr/team" element={
        <RoleRoute><IUPTeam /></RoleRoute>
      } />
      <Route path="/hr/iup-mall" element={
        <RoleRoute><IUPTemplateEditor /></RoleRoute>
      } />
      <Route path="/hr/forvantan" element={
        <RoleRoute><IUPExpectations /></RoleRoute>
      } />
      {/* Avgångstavla – alla inloggade inkl. marknadskonsulter */}
      <Route path="/avgangstavla" element={
        <RoleRoute><Avgangstavla /></RoleRoute>
      } />
      {/* WorkshopN – publik anmälningssida (ingen inloggning) */}
      <Route path="/workshopn/join/:workshopId" element={<WorkshopJoin />} />

      {/* WorkshopN – tillgängligt för alla inloggade användare */}
      <Route path="/workshopn" element={
        <RoleRoute><WorkshopNAdmin /></RoleRoute>
      } />
      <Route path="/workshopn/new" element={
        <RoleRoute><WorkshopNDesign /></RoleRoute>
      } />
      <Route path="/workshopn/:id/edit" element={
        <RoleRoute><WorkshopNDesign /></RoleRoute>
      } />
      <Route path="/workshopn/:id/intro" element={
        <RoleRoute><WorkshopNIntro /></RoleRoute>
      } />
      <Route path="/workshopn/:id/schedule" element={
        <RoleRoute><WorkshopNSchedule /></RoleRoute>
      } />
      <Route path="/workshopn/:id/enkät" element={
        <RoleRoute><WorkshopNEnkät /></RoleRoute>
      } />
      {/* Enkät public — token-baserad, ingen auth (båda URL-varianter) */}
      <Route path="/enkat/:workshopId/:enkatId/:deltagarId/:token" element={<EnkätSida />} />
      <Route path="/enkät/:workshopId/:enkatId/:deltagarId/:token" element={<EnkätSida />} />
      {/* Public views — no auth required */}
      <Route path="/workshopn/:id/present-public" element={
        <PublicViewRoute />
      } />
      <Route path="/workshopn/:id/schedule-public" element={
        <SchedulePublicViewRoute />
      } />
      <Route path="/workshopn/:id/schedule-speaker" element={
        <ScheduleSpeakerViewRoute />
      } />
      <Route path="/workshopn/:id/display" element={
        <WorkshopDisplayRoute />
      } />
      <Route path="/workshopn/:id/present" element={
        <RoleRoute><WorkshopNPresent /></RoleRoute>
      } />
      {/* Workshop deltagarvy – hanterar auth internt (inloggningsvy om ej inloggad) */}
      <Route path="/workshop/:sessionId" element={<WorkshopNParticipant />} />
      {/* Hybrid-utskrift – printbara svarblad med QR-koder */}
      <Route path="/workshopn/:id/print" element={
        <RoleRoute><WorkshopNPrint /></RoleRoute>
      } />
      <Route path="/workshopn/:id/kiosk" element={
        <RoleRoute><WorkshopNKiosk /></RoleRoute>
      } />
      {/* Hybrid-skanning – fotouppladdning och AI-tolkning (ingen auth) */}
      <Route path="/workshop-scan" element={<WorkshopScan />} />
      {/* SwotN – digital SWOT-analys */}
      <Route path="/swotn" element={
        <RoleRoute><SwotNAdmin /></RoleRoute>
      } />
      <Route path="/swotn/new" element={
        <RoleRoute><SwotNDesign /></RoleRoute>
      } />
      <Route path="/swotn/:id/edit" element={
        <RoleRoute><SwotNDesign /></RoleRoute>
      } />
      <Route path="/swotn/:id" element={
        <RoleRoute><SwotNBoard /></RoleRoute>
      } />
      <Route path="/swotn/:id/print" element={
        <RoleRoute><SwotNPrint /></RoleRoute>
      } />
      <Route path="/swot-scan" element={<SwotNScan />} />
      {/* PitchN – AI-drivna kundpresentationer */}
      <Route path="/pitch" element={<RoleRoute><PitchList /></RoleRoute>} />
      <Route path="/pitch/backgrounds" element={<RoleRoute><PitchBackgrounds /></RoleRoute>} />
      <Route path="/pitch/new" element={<RoleRoute><PitchBuilder /></RoleRoute>} />
      <Route path="/pitch/:pitchId/edit" element={<RoleRoute><PitchBuilder /></RoleRoute>} />
      <Route path="/pitch/:pitchId/present" element={<RoleRoute><PresenterView /></RoleRoute>} />
      {/* AudienceView – HDMI-fönster, ingen auth krävs */}
      <Route path="/pitch/:pitchId/audience" element={<AudienceView />} />
      <Route path="/pitch/:pitchId/view"   element={<RoleRoute><ExternalPitchView /></RoleRoute>} />
      <Route path="/pitch/:pitchId/review" element={<RoleRoute><PitchReview /></RoleRoute>} />
      {/* Publik delningslänk – ingen auth krävs */}
      <Route path="/p/:shareToken" element={<PublicPitchView />} />
      <Route path="/offer/:shareToken" element={<PublicOfferView />} />
      {/* AppLAB – intern verktygsplattform */}
      <Route path="/applab" element={<RoleRoute><AppLabLanding /></RoleRoute>} />
      <Route path="/applab/new" element={<RoleRoute><NewAppChoice /></RoleRoute>} />
      <Route path="/applab/admin" element={<RoleRoute><AppLabAdmin /></RoleRoute>} />
      <Route path="/applab/test-gateway" element={<RoleRoute><AppLabTestGateway /></RoleRoute>} />
      <Route path="/applab/upload" element={<RoleRoute><AppLabUpload /></RoleRoute>} />
      <Route path="/applab/docs" element={<RoleRoute><AppLabDocs /></RoleRoute>} />
      {/* MockupN */}
      <Route path="/applab/mockup" element={<RoleRoute><MockupLanding /></RoleRoute>} />
      <Route path="/applab/mockup/new" element={<RoleRoute><MockupWizard /></RoleRoute>} />
      <Route path="/applab/mockup/:appId/generating" element={<RoleRoute><MockupGenerating /></RoleRoute>} />
      <Route path="/applab/mockup/:appId/edit" element={<RoleRoute><MockupEditor /></RoleRoute>} />
      {/* MappN */}
      <Route path="/applab/mappn" element={<RoleRoute><MappNLanding /></RoleRoute>} />
      <Route path="/applab/mappn/:caseId/cards" element={<RoleRoute><MappNCards /></RoleRoute>} />
      <Route path="/applab/mappn/:caseId/perspectives" element={<RoleRoute><MappNPerspectives /></RoleRoute>} />
      <Route path="/applab/mappn/:caseId/map" element={<RoleRoute><MappNMap /></RoleRoute>} />
      <Route path="/applab/mappn/:caseId/table" element={<RoleRoute><MappNTable /></RoleRoute>} />
      <Route path="/applab/mappn/:caseId/tech" element={<RoleRoute><MappNTech /></RoleRoute>} />
      <Route path="/applab/mappn/:caseId/settings" element={<RoleRoute><MappNSettings /></RoleRoute>} />
    </Routes>
  );
}

// ── PWA Install Prompt ────────────────────────────────────────────────────────
const INSTALL_DISMISSED_KEY = 'lagbasn-install-dismissed';

function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [iosExpanded, setIosExpanded] = useState(false);

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
  const isSafariIOS = isIOS && /safari/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;

  useEffect(() => {
    if (isStandalone) return;
    if (localStorage.getItem(INSTALL_DISMISSED_KEY)) return;

    if (isIOS) {
      // iOS: show after 4s
      const t = setTimeout(() => setShow(true), 4000);
      return () => clearTimeout(t);
    }

    // Chrome/Edge: capture the beforeinstallprompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function dismiss(permanent = false) {
    setShow(false);
    if (permanent) localStorage.setItem(INSTALL_DISMISSED_KEY, '1');
  }

  async function handleInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismiss(outcome === 'accepted');
  }

  if (!show) return null;

  return (
    <Box sx={{
      position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, width: { xs: 'calc(100vw - 32px)', sm: 420 },
      bgcolor: '#1a1a1a', color: 'white',
      borderRadius: 2.5, p: 2.5,
      boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
      border: '1.5px solid #FF6B00',
    }}>
      {/* Close */}
      <Button size="small" onClick={() => dismiss(false)}
        sx={{ position: 'absolute', top: 6, right: 6, minWidth: 0, p: 0.5, color: '#888',
          '&:hover': { color: 'white' } }}>
        ✕
      </Button>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pr: 3 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 1.5, flexShrink: 0, overflow: 'hidden',
          bgcolor: '#FF6B00', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: 'white', lineHeight: 1 }}>N</Typography>
        </Box>
        <Box>
          <Typography fontWeight="bold" fontSize="0.95rem" sx={{ mb: 0.3 }}>
            Installera LagbasN som app
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa', lineHeight: 1.4, display: 'block' }}>
            Snabbare åtkomst, fungerar som en inbyggd app på telefon och dator.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        {/* Chrome/Edge: native install button */}
        {deferredPrompt && (
          <Button fullWidth variant="contained" onClick={handleInstall}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, fontWeight: 'bold', mb: 1 }}>
            Installera nu
          </Button>
        )}

        {/* iOS: expandable instructions */}
        {isIOS && !deferredPrompt && (
          <Box>
            <Button fullWidth variant="outlined" onClick={() => setIosExpanded(v => !v)}
              sx={{ borderColor: '#FF6B00', color: '#FF6B00', mb: iosExpanded ? 1 : 0 }}>
              Visa hur du installerar {iosExpanded ? '▲' : '▼'}
            </Button>
            {iosExpanded && (() => {
              const steps = isSafariIOS
                ? [
                    { step: '1', text: 'Tryck på dela-knappen (⎙) längst ner i Safari' },
                    { step: '2', text: 'Scrolla ner och tryck "Lägg till på hemskärmen"' },
                    { step: '3', text: 'Tryck "Lägg till" i övre högra hörnet' },
                  ]
                : [{ step: '!', text: 'Öppna sidan i Safari för att kunna installera appen på iOS.' }];
              return (
                <Box sx={{ bgcolor: '#2a2a2a', borderRadius: 1.5, p: 1.5, mt: 0.5 }}>
                  {steps.map(s => (
                    <Box key={s.step} sx={{ display: 'flex', gap: 1.5, mb: 0.8, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#FF6B00',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                        {s.step}
                      </Box>
                      <Typography variant="body2" sx={{ color: '#ddd', lineHeight: 1.4, pt: 0.2 }}>
                        {s.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })()}
          </Box>
        )}

        {/* No native prompt and not iOS: manual instructions */}
        {!deferredPrompt && !isIOS && (
          <Box sx={{ bgcolor: '#2a2a2a', borderRadius: 1.5, p: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 1 }}>
              Installera manuellt i din webbläsare:
            </Typography>
            {[
              { browser: 'Chrome / Edge (dator)', text: 'Klicka på ⊕-ikonen i adressfältet → "Installera"' },
              { browser: 'Android Chrome', text: 'Tryck på menyn ⋮ → "Lägg till på startskärmen"' },
            ].map(r => (
              <Box key={r.browser} sx={{ mb: 0.8 }}>
                <Typography variant="caption" sx={{ color: '#FF6B00', fontWeight: 700 }}>{r.browser}</Typography>
                <Typography variant="caption" sx={{ color: '#ccc', display: 'block' }}>{r.text}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Button size="small" onClick={() => dismiss(true)}
        sx={{ color: '#666', mt: 1, fontSize: '0.7rem', '&:hover': { color: '#aaa' } }}>
        Visa inte igen
      </Button>
    </Box>
  );
}

export default function App() {
  const [mode, setMode] = useState(
    localStorage.getItem('lagbasn-theme') || 'light'
  );

  function toggleMode(newMode) {
    setMode(newMode);
    localStorage.setItem('lagbasn-theme', newMode);
  }

  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: { main: '#1a1a1a' },
      secondary: { main: '#FF6B00' },
      ...ytor(mode),
      ...(mode === 'dark' ? {
        background: {
          default: '#000000',
          paper: '#1c1c1e',
        },
        text: {
          primary: '#f2f2f7',
          secondary: 'rgba(235,235,245,0.6)',
          disabled: 'rgba(235,235,245,0.3)',
        },
        divider: 'rgba(84,84,88,0.65)',
        action: {
          hover: 'rgba(255,255,255,0.06)',
          selected: 'rgba(255,255,255,0.12)',
          disabledBackground: 'rgba(255,255,255,0.08)',
        },
      } : {
        background: {
          default: '#f2f2f7',
          paper: '#ffffff',
        },
        divider: 'rgba(0,0,0,0.1)',
      })
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: '"Segoe UI", "Roboto", "Arial", sans-serif',
    },
    components: {
      MuiButton: {
        styleOverrides: {
          // Den svarta knappen (variant="contained"). I mörkt läge inverterad,
          // annars försvinner den mot pappret. Avstängd: dämpad men läsbar.
          containedPrimary: ({ theme: t }) => (t.palette.mode === 'dark' ? {
            backgroundColor: '#f2f2f7',
            color: '#000000',
            '&:hover': { backgroundColor: '#d1d1d6' },
            '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(235,235,245,0.5)' },
          } : {
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#333333' },
            '&.Mui-disabled': { backgroundColor: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.45)' },
          }),
          // Textknappar i primärfärg (#1a1a1a) syns inte på mörkt papper.
          textPrimary: ({ theme: t }) => (t.palette.mode === 'dark' ? { color: '#f2f2f7' } : {}),
          outlinedPrimary: ({ theme: t }) => (t.palette.mode === 'dark' ? {
            color: '#f2f2f7',
            borderColor: 'rgba(235,235,245,0.35)',
            '&:hover': {
              borderColor: 'rgba(235,235,245,0.6)',
              backgroundColor: 'rgba(255,255,255,0.06)',
            },
          } : {}),
        }
      },
      MuiChip: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: '#FF6B00',
            color: '#ffffff'
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: '#FF6B00'
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              color: '#FF6B00'
            }
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: '#FF6B00',
              '& + .MuiSwitch-track': {
                backgroundColor: '#FF6B00'
              }
            }
          }
        }
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: '#FF6B00'
            }
          }
        }
      },
      MuiLinearProgress: {
        styleOverrides: {
          barColorPrimary: {
            backgroundColor: '#FF6B00'
          }
        }
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            '&:before': { backgroundColor: 'transparent' }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            ...(t.palette.mode === 'dark' ? {
              backgroundImage: 'none',
              backgroundColor: '#1c1c1e',
              border: '1px solid rgba(84,84,88,0.5)',
              boxShadow: 'none',
            } : {
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }),
          }),
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            ...(t.palette.mode === 'dark' ? {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(84,84,88,0.65)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(174,174,178,0.8)',
              },
            } : {}),
          }),
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderColor: t.palette.mode === 'dark'
              ? 'rgba(84,84,88,0.65)'
              : 'rgba(0,0,0,0.1)',
          }),
        }
      },
    }
  }), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ThemeContextProvider mode={mode} setMode={toggleMode}>
        <AuthProvider>
          <FCMRegistrar />
          <Router>
            <GDPRGate>
              <AppRoutes />
            </GDPRGate>
            <InstallPrompt />
          </Router>
        </AuthProvider>
      </ThemeContextProvider>
    </ThemeProvider>
  );
}